import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Search, MapPin, ChevronRight, User, Clock, Briefcase,
  ArrowLeft, Plus, DollarSign, Calendar, Building2, Filter,
  SendHorizonal, CheckCircle2, X, Loader2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PostJobModal({ categories, onClose, onPosted }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ title: '', description: '', category_id: '', budget: '', budget_currency: 'USD', location: '', deadline: '', contact_email: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Başlık ve açıklama zorunlu'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/job-posts`, { ...form, budget: form.budget ? parseFloat(form.budget) : null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('İlan oluşturuldu!');
      onPosted();
    } catch { toast.error('İlan oluşturulamadı'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold">Yeni İş İlanı</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">İlan Başlığı *</label>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Örn. İstanbul Fuarı için 3D Tasarımcı aranıyor" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Açıklama *</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Proje detayları, beklentiler, gereksinimler..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 min-h-[100px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Kategori</label>
              <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400">
                <option value="">Tüm Kategoriler</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Bütçe</label>
              <div className="flex gap-1">
                <Input type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} placeholder="0" className="flex-1" />
                <select value={form.budget_currency} onChange={e => setForm(f => ({ ...f, budget_currency: e.target.value }))}
                  className="border border-gray-200 rounded-xl px-2 text-xs focus:outline-none">
                  {['USD','EUR','TRY','GBP','AED'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Konum</label>
              <Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Örn. İstanbul" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Son Başvuru</label>
              <Input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">İletişim E-postası</label>
            <Input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="ilan@sirket.com" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1">İptal</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <SendHorizonal className="w-4 h-4 mr-1" />}İlanı Yayınla
          </Button>
        </div>
      </div>
    </div>
  );
}

function ApplyModal({ post, onClose, onApplied }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ cover_letter: '', proposed_budget: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/job-posts/${post.id}/apply`, {
        cover_letter: form.cover_letter,
        proposed_budget: form.proposed_budget ? parseFloat(form.proposed_budget) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Başvurunuz gönderildi!');
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Başvuru gönderilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold">Başvur: {post.title}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Kapak Mektubu (opsiyonel)</label>
            <textarea value={form.cover_letter} onChange={e => setForm(f => ({ ...f, cover_letter: e.target.value }))}
              placeholder="Kendinizi ve bu iş için neden uygun olduğunuzu anlatın..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 min-h-[100px]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Teklifiniz (opsiyonel)</label>
            <div className="flex gap-1 items-center">
              <Input type="number" value={form.proposed_budget} onChange={e => setForm(f => ({ ...f, proposed_budget: e.target.value }))} placeholder="0" className="flex-1" />
              <span className="text-sm text-gray-500">{post.budget_currency || 'USD'}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1">İptal</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Başvur
          </Button>
        </div>
      </div>
    </div>
  );
}

function JobCard({ post, onApply }) {
  const { user } = useAuth();
  const isFreelancer = user?.org_memberships?.some(m => m.role?.startsWith('FREELANCER'));

  return (
    <div className="bg-white rounded-2xl border border-gray-100 hover:border-red-200 shadow-sm hover:shadow-md transition-all p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-gray-900 text-sm">{post.title}</h3>
            <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Açık</Badge>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{post.poster_name}</p>
        </div>
        {post.budget && (
          <span className="text-sm font-bold text-emerald-600 shrink-0">{post.budget} {post.budget_currency}</span>
        )}
      </div>

      <p className="text-gray-600 text-xs mt-3 line-clamp-2 leading-relaxed">{post.description}</p>

      <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-400">
        {post.location && (
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{post.location}</span>
        )}
        {post.deadline && (
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Son: {new Date(post.deadline).toLocaleDateString('tr-TR')}</span>
        )}
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString('tr-TR')}</span>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
        {post.category_id && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">{post.category_name || post.category_id}</span>
        )}
        <div className="ml-auto">
          {isFreelancer ? (
            <Button size="sm" onClick={() => onApply(post)} className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 px-3">
              <SendHorizonal className="w-3 h-3 mr-1" />Başvur
            </Button>
          ) : (
            <Link to="/freelancer/signup">
              <Button size="sm" variant="outline" className="text-xs h-7 border-red-200 text-red-600 hover:bg-red-50">
                Freelancer Ol → Başvur
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function JobBoardPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [showPostModal, setShowPostModal] = useState(false);
  const [applyModal, setApplyModal] = useState(null);

  const isLoggedIn = !!user;

  useEffect(() => {
    fetch(`${API}/public/freelancer-categories`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setCategories(d?.categories || []))
      .catch(() => {});
  }, []);

  const fetchPosts = (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 20 });
    if (query) params.set('q', query);
    if (category) params.set('category', category);
    fetch(`${API}/job-posts?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setPosts(d?.items || []); setTotal(d?.total || 0); setPage(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPosts(1); }, [query, category]);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-950 via-red-950 to-gray-900 py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />Ana Sayfa
          </button>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-red-400 block mb-2">İş İlanları</span>
              <h1 className="text-4xl sm:text-5xl font-black text-white">Freelancer İlan Panosu</h1>
              <p className="text-gray-400 text-sm mt-2 max-w-xl">Stand ve etkinlik sektöründe iş ilanlarını keşfet. Firmalar freelancer arar, sen başvur.</p>
            </div>
            {isLoggedIn && (
              <Button onClick={() => setShowPostModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white shrink-0 gap-2">
                <Plus className="w-4 h-4" />İlan Oluştur
              </Button>
            )}
          </div>
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-sm">
            {[
              { value: total, label: 'Açık İlan' },
              { value: categories.length, label: 'Kategori' },
              { value: '24s', label: 'Yanıt Süresi' },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <p className="text-2xl font-black text-red-400">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input value={query} onChange={e => setQuery(e.target.value)}
              placeholder="İlan ara..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-red-400" />
          </div>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="text-sm border border-gray-200 rounded-xl px-4 py-2 bg-white focus:outline-none focus:border-red-400">
            <option value="">Tüm Kategoriler</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {(query || category) && (
            <button onClick={() => { setQuery(''); setCategory(''); }}
              className="text-sm text-red-600 hover:text-red-800 px-3 py-2 rounded-xl border border-red-200">
              Temizle
            </button>
          )}
          <span className="ml-auto text-sm text-gray-500 flex items-center">
            <strong className="text-gray-900 mr-1">{total}</strong> ilan
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-1">Şu an açık ilan yok</h3>
            <p className="text-gray-500 text-sm">Filtrelerinizi değiştirin veya daha sonra tekrar bakın.</p>
            {isLoggedIn && (
              <Button onClick={() => setShowPostModal(true)} className="mt-4 bg-red-600 hover:bg-red-700 text-white gap-2">
                <Plus className="w-4 h-4" />İlk İlanı Oluştur
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {posts.map(p => (
              <JobCard key={p.id} post={p} onApply={setApplyModal} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button onClick={() => fetchPosts(page - 1)} disabled={page === 1}
              className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:border-red-400 transition-colors bg-white">
              Önceki
            </button>
            <span className="text-sm text-gray-500">{page} / {totalPages}</span>
            <button onClick={() => fetchPosts(page + 1)} disabled={page === totalPages}
              className="px-4 py-2 text-sm border border-gray-200 rounded-xl disabled:opacity-40 hover:border-red-400 transition-colors bg-white">
              Sonraki
            </button>
          </div>
        )}

        {/* CTA */}
        {!isLoggedIn && (
          <div className="mt-12 bg-gradient-to-r from-red-600 to-rose-700 rounded-3xl p-8 text-center text-white">
            <h3 className="text-2xl font-black mb-2">Freelancer mısın?</h3>
            <p className="text-red-200 text-sm mb-5">Hesap oluştur, ilanlara başvur, iş al.</p>
            <Link to="/freelancer/signup">
              <Button className="bg-white text-red-700 hover:bg-red-50 font-bold px-8">Ücretsiz Kaydol →</Button>
            </Link>
          </div>
        )}
      </div>

      {showPostModal && (
        <PostJobModal categories={categories} onClose={() => setShowPostModal(false)} onPosted={() => { setShowPostModal(false); fetchPosts(1); }} />
      )}
      {applyModal && (
        <ApplyModal post={applyModal} onClose={() => setApplyModal(null)} onApplied={() => { setApplyModal(null); toast.success('Başvurunuz alındı!'); }} />
      )}
    </div>
  );
}
